import React, { forwardRef } from 'react';

import DOMPurify from 'dompurify';

import { Box } from '@mui/material';

interface Props {
  certificateHtml: string;
  title?: string;
  height?: string;
}

/**
 * Renders sanitized ATOL certificate HTML inside an iframe for full style isolation.
 * Accepts raw HTML — sanitization with DOMPurify is handled internally.
 * Exposes the iframe element via ref so callers can access the rendered content
 * (e.g. for PDF generation without re-parsing).
 */
const AtolCertificateHtmlViewer = forwardRef<HTMLIFrameElement, Props>(function AtolCertificateHtmlViewer(
  { certificateHtml, title = 'ATOL Certificate', height = '800px' },
  ref,
) {
  const sanitizedHtml = DOMPurify.sanitize(certificateHtml, {
    WHOLE_DOCUMENT: true,
    FORCE_BODY: true,
  });

  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'auto',
        bgcolor: 'white',
        p: 3,
      }}
    >
      <Box
        component="iframe"
        ref={ref}
        srcDoc={sanitizedHtml}
        title={title}
        sx={{
          width: '100%',
          height,
          border: 'none',
          display: 'block',
        }}
      />
    </Box>
  );
});

export default AtolCertificateHtmlViewer;
